import React, { useEffect, useRef } from 'react';
import './SharedModal.css';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  headerActions?: React.ReactNode;
  footerActions?: React.ReactNode;
  preventScroll?: boolean;
}

export const SharedModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  headerActions,
  footerActions,
  preventScroll = true
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeOnEscape, onClose]);

  // Handle body scroll prevention
  useEffect(() => {
    if (isOpen && preventScroll) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      
      // Focus management
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements && focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }

      return () => {
        document.body.style.overflow = '';
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, preventScroll]);

  // Handle overlay click
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`modal-overlay ${size}`} 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        ref={modalRef}
        className={`modal-content ${size} ${className}`}
        role="document"
      >
        {/* Header */}
        <div className="modal-header">
          <h2 id="modal-title" className="modal-title">
            {title}
          </h2>
          <div className="modal-header-actions">
            {headerActions}
            {showCloseButton && (
              <button
                type="button"
                className="modal-close-button"
                onClick={onClose}
                aria-label="Close modal"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="modal-body">
          {children}
        </div>

        {/* Footer */}
        {footerActions && (
          <div className="modal-footer">
            {footerActions}
          </div>
        )}
      </div>
    </div>
  );
};

// Specialized modal types for common use cases
export interface ConfirmModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger' | 'warning';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary'
}) => {
  const handleConfirm = () => {
    onConfirm();
    onCancel(); // Close modal
  };

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      size="small"
      footerActions={
        <div className="modal-confirm-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button 
            type="button" 
            className={`btn btn-${confirmVariant}`}
            onClick={handleConfirm}
          >
            {confirmText}
          </button>
        </div>
      }
    >
      <p className="modal-message">{message}</p>
    </SharedModal>
  );
};

export interface SaveModalProps {
  isOpen: boolean;
  onSave: (name: string, description: string) => void;
  onCancel: () => void;
  title?: string;
  nameLabel?: string;
  descriptionLabel?: string;
  saveText?: string;
  cancelText?: string;
  initialName?: string;
  initialDescription?: string;
}

export const SaveModal: React.FC<SaveModalProps> = ({
  isOpen,
  onSave,
  onCancel,
  title = 'Save Scenario',
  nameLabel = 'Scenario Name',
  descriptionLabel = 'Description (Optional)',
  saveText = 'Save',
  cancelText = 'Cancel',
  initialName = '',
  initialDescription = ''
}) => {
  const [name, setName] = React.useState(initialName);
  const [description, setDescription] = React.useState(initialDescription);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [isOpen, initialName, initialDescription]);

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim(), description.trim());
      onCancel(); // Close modal
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && name.trim()) {
      handleSave();
    }
  };

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      size="medium"
      footerActions={
        <div className="modal-save-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!name.trim()}
          >
            {saveText}
          </button>
        </div>
      }
    >
      <div className="save-modal-form">
        <div className="form-group">
          <label htmlFor="scenario-name">{nameLabel}</label>
          <input
            id="scenario-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter scenario name..."
            className="form-control"
            autoFocus
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="scenario-description">{descriptionLabel}</label>
          <textarea
            id="scenario-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description..."
            className="form-control"
            rows={3}
          />
        </div>
      </div>
    </SharedModal>
  );
};

export interface DisclaimerModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  title?: string;
  content: string | React.ReactNode;
  acceptText?: string;
  declineText?: string;
}

export const DisclaimerModal: React.FC<DisclaimerModalProps> = ({
  isOpen,
  onAccept,
  onDecline,
  title = 'Important Disclaimer',
  content,
  acceptText = 'I Understand',
  declineText = 'Cancel'
}) => {
  return (
    <SharedModal
      isOpen={isOpen}
      onClose={onDecline}
      title={title}
      size="large"
      closeOnOverlayClick={false}
      closeOnEscape={false}
      showCloseButton={false}
      footerActions={
        <div className="modal-disclaimer-actions">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onDecline}
          >
            {declineText}
          </button>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={onAccept}
          >
            {acceptText}
          </button>
        </div>
      }
    >
      <div className="disclaimer-content">
        {typeof content === 'string' ? (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          content
        )}
      </div>
    </SharedModal>
  );
};

// Hook for managing modal state
export const useModal = (initialState: boolean = false) => {
  const [isOpen, setIsOpen] = React.useState(initialState);

  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen(prev => !prev), []);

  return {
    isOpen,
    open,
    close,
    toggle
  };
};

export default SharedModal;